import {
	AfterViewInit,
	Component,
	EventEmitter,
	Input,
	OnInit,
	Output,
	ViewChild
}                                 from '@angular/core';
import { UntypedFormControl }     from '@angular/forms';
import {
	FormControlBase,
	FormControlBaseComponent
}                                 from '@app/core/bases/form-control-base/form-control-base.component';
import {
	NgbDateAdapter,
	NgbDateNativeUTCAdapter,
	NgbDatepickerConfig,
	NgbDatepickerI18n,
	NgbDateStruct,
	NgbInputDatepicker
}                                 from '@ng-bootstrap/ng-bootstrap';
import { PlacementArray }         from '@ng-bootstrap/ng-bootstrap/util/positioning';
import { CalendarModeEnum }       from '@shared/components/date-picker/date-picker.model';
import { MonthPickerComponent }   from '@shared/components/month-picker/month-picker.component';
import { IPreselectedDateItem }   from '@shared/components/month-picker/month-picker.model';
import { CustomDatepickerI18n }   from '@shared/services/ngb-datepicker-custom-services/custom-datepicker-i18n.service';
import { DateUtils }              from '@utils/helpers';
import { DatePrecisionLevelEnum } from '@utils/helpers/date-utils';
import {
	distinctUntilChanged,
	takeUntil
}                                 from 'rxjs/operators';

@Component( {
	selector: 'app-form-datepicker[formEntityId][formGroup][config]',
	templateUrl: './form-datepicker.component.html',
	styleUrls: [ './form-datepicker.component.scss' ],
	providers: [
		NgbDatepickerConfig,
		{ provide: NgbDatepickerI18n, useClass: CustomDatepickerI18n },
		{ provide: NgbDateAdapter, useClass: NgbDateNativeUTCAdapter }
	]
} )
export class FormDatepickerComponent extends FormControlBaseComponent implements FormControlBase, AfterViewInit, OnInit {

	private _datePicker: NgbInputDatepicker;
	private _maxDate: NgbDateStruct;
	private _minDate: NgbDateStruct;
	private _monthPicker: MonthPickerComponent;

	public readonly calendarModeEnum = CalendarModeEnum;
	public controlValue: Date;
	public formControl: UntypedFormControl;
	public monthPickerMinDate: Date;
	public revertValue: Date;

	// Bindings
	@Input() datepickerPopupPlacement: PlacementArray = 'bottom-end top-end bottom top bottom-start top-start end start';

	@Input() set maxDate( maxDate: NgbDateStruct | Date ) {
		this._maxDate = maxDate instanceof Date
										? DateUtils.dateToNgbDateStruct( maxDate )
										: maxDate;
	}

	get maxDate(): NgbDateStruct {
		return this._maxDate;
	}

	@Input() set minDate( minDate: NgbDateStruct | Date ) {
		if ( !minDate ) { return; }

		if ( this.calendarMode === CalendarModeEnum.MONTH ) {
			this.monthPickerMinDate = minDate instanceof Date
																? minDate
																: DateUtils.ngbDateStructToDate( minDate, DatePrecisionLevelEnum.MONTH );
			return;
		}

		this._minDate = minDate instanceof Date
										? DateUtils.dateToNgbDateStruct( minDate )
										: minDate;
	}

	get minDate(): NgbDateStruct {
		return this._minDate;
	}

	@Input() calendarMode: CalendarModeEnum = CalendarModeEnum.DAY;
	@Input() dateInputFormat: Intl.DateTimeFormatOptions;
	@Input() preselectedDateList: IPreselectedDateItem[];

	@Output() changeEventEmitter: EventEmitter<Date> = new EventEmitter<Date>();

	@ViewChild( 'datepicker' ) set datePicker( datePicker: NgbInputDatepicker ) {
		this._datePicker = datePicker;
	}

	@ViewChild( 'monthPicker' ) set monthPicker( monthPicker: MonthPickerComponent ) {
		this._monthPicker = monthPicker;
	}

	constructor() {
		super();
	}

	ngOnInit(): void {
		super.ngOnInit();
		this.control?.updateValueAndValidity();

		this.control?.valueChanges
		.pipe(
			distinctUntilChanged(),
			takeUntil( this.ngUnsubscribe )
		)
		.subscribe( val => {

			const timeout = setTimeout( () => {
				clearTimeout( timeout );
				this.controlValue = val instanceof Date
														? new Date( val?.valueOf() )
														: val;

				this.changeEventEmitter.emit( this.controlValue );
			}, 0 );

			this.updateActionButtonsVisibility( !!val );
			this.updateRevertValue( val );
		} );
	}

	ngAfterViewInit(): void {
		super.ngAfterViewInit();
	}

	close(): void {
		switch ( this.calendarMode ) {
			case CalendarModeEnum.DAY:
				this._datePicker.close();
				break;

			case CalendarModeEnum.MONTH:
				this._monthPicker.isOpen = false;
				break;
		}
	}

	open(): void {
		switch ( this.calendarMode ) {
			case CalendarModeEnum.DAY:
				this._datePicker.open();
				break;

			case CalendarModeEnum.MONTH:
				this._monthPicker.isOpen = true;
				break;
		}
	}

	setDate( datePicker: NgbInputDatepicker ): void {
		datePicker.toggle();
	}

	setSelectedDate( date ) {
		if ( !isNaN( date?.getTime() ) && date?.toISOString() !== this.control?.value?.toISOString() ) {
			this.control.setValue( date );
			this.control?.markAsTouched();
			this.control?.markAsDirty();
		}
	}

	updateRevertValue( val: Date ): void {
		if ( val ) { this.revertValue = val; }
	}
}
